import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import {
  StudentFinancialAidDataReturn,
  CourseComboReturn,
  StudentTermByTerm,
  FticCourseTrendData,
  FticCourseFilters,
  StudentNameAndID,
  EntryExitFilters,
  ProgramBottleneckCourseFilters,
  APRGraduationSummaryFilters,
  AtRiskStudentReturn,
  RiskCommentInfo,
  CourseInfoExtended,
  TargetCourseCombined,
  ProgramBottleneckCourseExtended,
  TakenCourseAndGrade,
  APRGraduationSummary,
  EntryExit,
  TransferStudentReturn,
  TransferStudentFilterOptions,
} from '../../../server/types';
import {
  AtRiskStudentRequest,
  FinancialAidQuery,
  ProgramBottleneckCoursesQuery,
  StudentQuery,
  APRGraduationSummaryQuery,
  EntryExitQuery,
  TakenCoursesAndGradesQuery,
  FticCourseInfoQuery,
  FticCourseInfoFiltersQuery,
  AtRiskCommentRequest,
  GradePredictionQuery,
  CourseRecommenderQuery,
  TargetCourseQuery,
  CourseGradeEstimationResponse,
  CourseComboQuery,
  CohortQuery,
  StudentCourseExplorerParamsResponse,
} from '../../../server/schemas';
import { HttpService } from './http.service';
import { FeedbackPostBody } from 'src/server/schemas/common';

@Injectable({
  providedIn: 'root',
})
export class PamlabApiServiceService {
  private studentsSubject = new BehaviorSubject<StudentNameAndID[]>([]);
  students$: Observable<StudentNameAndID[]> =
    this.studentsSubject.asObservable();

  constructor(private httpService: HttpService) {}

  encode(params: { [key: string]: string | string[] }) {
    for (const [key, value] of Object.entries(params)) {
      if (typeof value === 'object') {
        for (let i = 0; i < value.length; i++) {
          value[i] = encodeURIComponent(value[i]);
        }
      } else {
        params[key] = encodeURIComponent(value);
      }
    }
    return params;
  }

  // get apr trends
  getAPRTrendsWithParams(aprTrendValues: APRGraduationSummaryQuery) {
    const params = new HttpParams().appendAll({
      ...JSON.parse(JSON.stringify(aprTrendValues)),
    });

    return this.httpService.get<APRGraduationSummary>(
      `/apr-graduation-summary`,
      params,
    );
  }

  // get entry/exit flow
  getEntryExitFlow(entryExitFlowParams: EntryExitQuery) {
    return this.httpService.get<EntryExit[]>(
      `/entry-exit-flow`,
      new HttpParams().appendAll({ ...entryExitFlowParams }),
    );
  }

  // get course combinations
  getCourseCombo(courseComboValues: CourseComboQuery) {
    return this.httpService.get<CourseComboReturn>(
      `/course-combinations`,
      new HttpParams().appendAll({ ...courseComboValues }),
    );
  }

  // get killer courses
  getProgramBottleneck(query: ProgramBottleneckCoursesQuery) {
    return this.httpService.get<ProgramBottleneckCourseExtended[]>(
      `/program-bottleneck-courses`,
      new HttpParams().appendAll({ ...query }),
    );
  }

  // get recommended courses
  getRecommededCourses(recommendedCoursesValues: CourseRecommenderQuery) {
    const params = JSON.parse(JSON.stringify(recommendedCoursesValues));

    return this.httpService.get<CourseInfoExtended[]>(
      `/course-recommender`,
      new HttpParams().appendAll({ ...this.encode(params) }),
    );
  }

  //get course catalog
  getCourseCatalog() {
    return this.httpService.get(`/courses`);
  }

  // get all at risk student
  getAtRiskStudents() {
    return this.httpService.get<AtRiskStudentReturn[]>(`/at-risk-students`);
  }

  // get all at risk student
  checkAtRiskStudent(uwf_id: any) {
    return this.httpService.get(
      `/at-risk-students`,
      new HttpParams().appendAll({ uwf_id }),
    );
  }

  // post student info for at risk student
  markAtRiskStudent(studentValues: AtRiskStudentRequest) {
    return this.httpService.post<AtRiskStudentReturn>(
      `/at-risk-students`,
      studentValues,
    );
  }

  // post student info for at risk student
  addAtRiskCommentsToStudent(riskCommentsValues: AtRiskCommentRequest) {
    return this.httpService.post<RiskCommentInfo | HttpErrorResponse>(
      `/risk-comments`,
      riskCommentsValues,
    );
  }

  // delete at risk student (req.params)
  deleteAtRiskStudent(id: number) {
    return this.httpService.delete(`/at-risk-students/${id}`);
  }

  deleteAtRiskStudentComment(id: number) {
    return this.httpService.delete(`/risk-comments/${id}`);
  }

  // get student info (req.query)
  getStudent(studentValues: StudentQuery) {
    return this.httpService.get<StudentTermByTerm>(
      `/student-profile`,
      new HttpParams().appendAll({ ...studentValues }),
    );
  }

  // get cohort info
  getCohort(cohortValues: CohortQuery) {
    return this.httpService.get<any>(
      `/cohort-profile`,
      new HttpParams().appendAll({ ...cohortValues }),
    );
  }

  // get course estimation grade
  getCourseEstimation(courseGradeEstimationValues: GradePredictionQuery) {
    const queryString = JSON.parse(JSON.stringify(courseGradeEstimationValues));

    return this.httpService.get<CourseGradeEstimationResponse>(
      `/course-grade-estimation`,
      new HttpParams().appendAll({ ...this.encode(queryString) }),
    );
  }

  // get courses taken
  getCoursesTaken(params: TakenCoursesAndGradesQuery) {
    return this.httpService.get<TakenCourseAndGrade[]>(
      `/taken-courses-and-grades`,
      new HttpParams().appendAll({ ...params }),
    );
  }

  // get courses cohort and program of student for course grade estimation
  getCohortProgramOfStudent(uwf_id: number) {
    return this.httpService.get<StudentCourseExplorerParamsResponse>(
      `/course-explorer-params`,
      new HttpParams().appendAll({ uwf_id }),
    );
  }

  getTargetCourse(target: TargetCourseQuery) {
    return this.httpService.get<TargetCourseCombined>(
      `/prerequisite-impact`,
      new HttpParams().appendAll({ ...target }),
    );
  }

  getFinancialAidData(
    params: FinancialAidQuery,
  ): Observable<StudentFinancialAidDataReturn> {
    return this.httpService.get<StudentFinancialAidDataReturn>(
      `/financial-aid`,
      new HttpParams().appendAll({ ...params }),
    );
  }

  getFticCourseData(params: FticCourseInfoQuery) {
    return this.httpService.get<FticCourseTrendData>(
      `/ftic-course-dwf-apr`,
      new HttpParams().appendAll({ ...params }),
    );
  }

  getFticCourseDataFilters(params: FticCourseInfoFiltersQuery) {
    return this.httpService.get<FticCourseFilters>(
      `/ftic-course-dwf-apr-filters`,
      new HttpParams().appendAll({ ...params }),
    );
  }

  fetchStudentNamesAndIDs(): Observable<StudentNameAndID[]> {
    if (this.studentsSubject.getValue().length === 0) {
      return this.httpService
        .get<StudentNameAndID[]>(`/all-students`)
        .pipe(tap((students) => this.studentsSubject.next(students)));
    } else {
      return this.students$;
    }
  }

  resetStudents() {
    this.studentsSubject.next([]);
  }

  fetchEntryExitFilters(): Observable<EntryExitFilters> {
    return this.httpService.get<EntryExitFilters>(`/entry-exit-flow-filters`);
  }

  fetchProgramBottleneckFilters(): Observable<ProgramBottleneckCourseFilters> {
    return this.httpService.get<ProgramBottleneckCourseFilters>(
      `/program-bottleneck-courses-filters`,
    );
  }

  fetchAPRGraduationSummaryFilters(): Observable<APRGraduationSummaryFilters> {
    return this.httpService.get<APRGraduationSummaryFilters>(
      `/apr-graduation-summary-filters`,
    );
  }

  fetchTransferEnrollmentTrends(
    params?: any,
  ): Observable<TransferStudentReturn[]> {
    return this.httpService.get<TransferStudentReturn[]>(
      `/transfer-enrollment-trends`,
      new HttpParams().appendAll({ ...params }),
    );
  }

  fetchTransferEnrollmentTrendsFilterOptions(): Observable<TransferStudentFilterOptions> {
    return this.httpService.get<TransferStudentFilterOptions>(
      `/transfer-enrollment-trends-filters`,
    );
  }

  saveFeedback(feedback: FeedbackPostBody): Observable<any> {
    return this.httpService.post<any>(`/feedback`, feedback);
  }
}
